import { useEffect, useState } from "react";
import {
  ApiError,
  WEBSHOP_EVENT_TYPES,
  fetchWebshopEventStats,
  fetchWebshopEvents,
} from "@/lib/api";
import type { WebshopEvent, WebshopEventStats } from "@/lib/api";
import type { ColumnDef } from "@tanstack/react-table";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { NativeSelect } from "@/components/ui/native-select";
import { DataTable, TablePager } from "@/components/emc/DataTable";
import { PageHeader } from "@/components/emc/PageHeader";
import { StatGrid, StatTile } from "@/components/emc/StatTile";
import { StatusPill } from "@/components/emc/StatusPill";
import { EnumText } from "@/components/emc/EnumText";
import { count, minute, shareOf } from "@/components/emc/format";
import { labelOf } from "@/components/emc/status";
import { ToolbarForm } from "@/components/emc/Toolbar";
import { EmptyState, ErrorState } from "@/components/emc/states";
import { Country } from "@/components/emc/Flag";
import { KlaviyoSignalSources } from "@/pages/events/KlaviyoSignalSources";

// The same nine markets the rest of the product knows about.
const COUNTRIES = ["RO", "PL", "HU", "GR", "SK", "EU", "BG", "IT", "CZ"];
const PAGE_SIZE = 50;

function errorMessage(error: unknown): string {
  if (error instanceof ApiError) return error.message;
  if (error instanceof Error) return error.message;
  return "Ismeretlen hiba.";
}

const COLUMNS: ColumnDef<WebshopEvent>[] = [
  {
    id: "when",
    header: "Mikor",
    cell: ({ row }) => minute(row.original.occurred_at),
    meta: { mono: true, muted: true },
  },
  { id: "country", header: "Ország", cell: ({ row }) => <Country code={row.original.country} /> },
  {
    id: "type",
    header: "Típus",
    cell: ({ row }) => <EnumText domain="webshopEvent" value={row.original.event_type} />,
  },
  { id: "email", header: "Email", cell: ({ row }) => row.original.email_normalized },
  {
    id: "contact",
    header: "Kontakt",
    cell: ({ row }) =>
      row.original.contact_id === null ? (
        <span className="text-muted-foreground">nincs</span>
      ) : (
        <span className="font-mono">{row.original.contact_id}</span>
      ),
  },
  {
    id: "external",
    header: "Shop-azonosító",
    cell: ({ row }) => row.original.external_id,
    meta: { mono: true },
  },
  {
    id: "stream",
    header: "Stream",
    cell: ({ row }) => <StatusPill domain="stream" state={row.original.stream_state} />,
  },
];

export default function Events() {
  const [country, setCountry] = useState("");
  const [type, setType] = useState("");
  const [email, setEmail] = useState("");
  const [since, setSince] = useState("");
  const [until, setUntil] = useState("");

  const [rows, setRows] = useState<WebshopEvent[]>([]);
  const [cursor, setCursor] = useState<number | null>(null);
  const [nextCursor, setNextCursor] = useState<number | null>(null);
  const [stats, setStats] = useState<WebshopEventStats | null>(null);
  const [openId, setOpenId] = useState<number | null>(null);
  const [listError, setListError] = useState<string | null>(null);

  function load(nextPage: number | null) {
    fetchWebshopEvents({
      country,
      type,
      email,
      since: since === "" ? "" : new Date(since).toISOString(),
      until: until === "" ? "" : new Date(until).toISOString(),
      cursor: nextPage,
      limit: PAGE_SIZE,
    })
      .then((body) => {
        setRows(body.items);
        setNextCursor(body.next_cursor);
        setCursor(nextPage);
        setListError(null);
      })
      .catch((error: unknown) => setListError(errorMessage(error)));
  }

  useEffect(() => {
    load(null);
    fetchWebshopEventStats({ country, hours: 24 })
      .then(setStats)
      .catch((error: unknown) => setListError(errorMessage(error)));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [country, type]);

  function onSearch(event: React.FormEvent) {
    event.preventDefault();
    load(null);
  }

  return (
    <>
      <PageHeader
        title="Webshop-események"
        description={
          <>
            Amit a webshop küld: regisztráció, kosárelhagyás, rendelés. Ezek a flow-k
            triggerei. A sor kinyitva megmutatja, mit küldött pontosan a shop.
          </>
        }
      />

      {stats !== null && (
        <StatGrid className="mb-6">
          <StatTile size="sm" label="Az utolsó 24 óra" value={count(stats.total)} comparison="esemény" />
          {WEBSHOP_EVENT_TYPES.filter((name) => stats.by_type[name]).map((name) => (
            <StatTile
              key={name}
              size="sm"
              label={labelOf("webshopEvent", name)}
              value={count(stats.by_type[name])}
              comparison={shareOf(stats.by_type[name], stats.total, "az összes")}
            />
          ))}
          <StatTile
            size="sm"
            label="Kontakt nélkül"
            value={count(stats.unmatched)}
            comparison={stats.unmatched > 0 ? "a címhez nincs kontakt az országban" : "mind párosítva"}
          />
          <StatTile
            size="sm"
            label="Streamre vár"
            value={count(stats.stream_pending)}
            tone={stats.stream_pending > 0 ? "warn" : "muted"}
            comparison={stats.stream_pending > 0 ? "a reconcile teszi fel" : "mind kiment"}
          />
        </StatGrid>
      )}

      <ToolbarForm className="mb-3" onSubmit={onSearch}>
        <NativeSelect
          aria-label="Ország"
          value={country}
          onChange={(event) => setCountry(event.target.value)}
        >
          <option value="">Minden ország</option>
          {COUNTRIES.map((code) => (
            <option key={code} value={code}>
              {code}
            </option>
          ))}
        </NativeSelect>
        <NativeSelect aria-label="Típus" value={type} onChange={(event) => setType(event.target.value)}>
          <option value="">Minden típus</option>
          {WEBSHOP_EVENT_TYPES.map((name) => (
            <option key={name} value={name}>
              {labelOf("webshopEvent", name)}
            </option>
          ))}
        </NativeSelect>
        <Input
          aria-label="Email"
          className="w-56"
          placeholder="Email"
          value={email}
          onChange={(event) => setEmail(event.target.value)}
        />
        <Input
          aria-label="Ettől"
          className="w-auto"
          type="datetime-local"
          value={since}
          onChange={(event) => setSince(event.target.value)}
        />
        <Input
          aria-label="Eddig"
          className="w-auto"
          type="datetime-local"
          value={until}
          onChange={(event) => setUntil(event.target.value)}
        />
        <Button type="submit">Keresés</Button>
      </ToolbarForm>

      {listError !== null && <ErrorState message={listError} className="mb-4" />}

      <DataTable
        label="Webshop-események"
        columns={COLUMNS}
        data={rows}
        getRowId={(row) => String(row.id)}
        onRowClick={(row) => setOpenId(openId === row.id ? null : row.id)}
        isRowSelected={(row) => row.id === openId}
        renderExpanded={(row) =>
          openId === row.id ? (
            <div className="flex flex-col gap-2">
              <p className="text-muted-foreground">
                Forrás: {row.source} · beérkezett:{" "}
                <span className="data-mono">{minute(row.received_at)}</span> · batch:{" "}
                <span className="font-mono">{row.ingest_id}</span>
              </p>
              <pre className="m-0 max-h-80 overflow-auto rounded-control border border-line bg-surface p-3 text-label">
                {JSON.stringify(row.payload, null, 2)}
              </pre>
            </div>
          ) : null
        }
        empty={<EmptyState bare title="Nincs találat." />}
        footer={
          <TablePager>
            <Button
              variant="outline"
              type="button"
              disabled={cursor === null}
              onClick={() => load(null)}
            >
              Elejére
            </Button>
            <Button
              variant="outline"
              type="button"
              disabled={nextCursor === null}
              onClick={() => load(nextCursor)}
            >
              Következő lap
            </Button>
          </TablePager>
        }
      />

      <KlaviyoSignalSources />
    </>
  );
}
